import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type ChatBan, type ChatFeed, type ChatModerator, type ChatOwnerMessage, type ChatWords, type Stream } from '../api';
import { CopyField, Tip } from '../ui';

/**
 * Live chat moderation on the stream page (docs.justonair.com/chat): the
 * owner's feed (every message, hidden ones too, with what is known about the
 * sender, never the IP), posting as host or moderator, delete, ban, pin, slow
 * mode, pause, clear, the ban list and the word list. Polls every 2 s while
 * the stream can still have chat; a finished stream's log is read once.
 */
const EMOJI: Record<string, string> = { heart: '❤️', clap: '👏', laugh: '😂', fire: '🔥', party: '🎉', wow: '😮' };
const SLOW = [0, 5, 10, 30, 60, 120, 300];
const KEEP = 1000;
const STATE_TEXT: Record<string, string> = {
  open: 'Open',
  paused: 'Paused',
  read_only: 'Read-only (ended)',
  closed: 'Closed',
  off: 'Off',
};

type Header = Omit<ChatFeed, 'messages'>;

function merge(prev: ChatOwnerMessage[], fresh: ChatOwnerMessage[], deleted: string[], clearedAt: string | null): ChatOwnerMessage[] {
  const byId = new Map(prev.map((m) => [m.id, m]));
  for (const m of fresh) byId.set(m.id, m);
  const now = new Date().toISOString();
  for (const id of deleted) {
    const m = byId.get(id);
    if (m && !m.deleted_at) byId.set(id, { ...m, deleted_at: now, visible: false });
  }
  if (clearedAt) {
    const c = Date.parse(clearedAt);
    for (const [id, m] of byId) if (!m.cleared && Date.parse(m.created_at) <= c) byId.set(id, { ...m, cleared: true, visible: false });
  }
  return [...byId.values()].sort((a, b) => a.seq - b.seq).slice(-KEEP);
}

const errText = (err: unknown) => (err instanceof ApiError ? err.message : 'That did not work. Try again.');

export function ChatCard({ stream }: { stream: Stream }) {
  const id = encodeURIComponent(stream.id);
  const running = stream.status === 'live' || stream.status === 'pending' || (stream.ended_at !== null && Date.now() - Date.parse(stream.ended_at) < 15 * 60_000);
  const [head, setHead] = useState<Header | null>(null);
  const [msgs, setMsgs] = useState<ChatOwnerMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [role, setRole] = useState<'host' | 'moderator'>('host');
  const [hidden, setHidden] = useState(true);
  const [panel, setPanel] = useState<'bans' | 'words' | 'mods' | null>(null);
  const [exporting, setExporting] = useState(false);
  const after = useRef(0);
  const list = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  const load = useCallback(async () => {
    for (let page = 0; page < 5; page++) {
      const f = await api<ChatFeed>(`/v1/streams/${id}/chat?after=${after.current}&limit=500`);
      after.current = f.next_after;
      const { messages, ...rest } = f;
      setHead(rest);
      setMsgs((prev) => merge(prev, messages, f.deleted_ids, f.cleared_at));
      if (!f.has_more) return;
    }
  }, [id]);

  useEffect(() => {
    let stop = false;
    let timer = 0;
    const tick = async () => {
      try {
        await load();
        setError(null);
      } catch (err) {
        setError(errText(err));
      }
      if (!stop && running) timer = window.setTimeout(tick, document.hidden ? 10_000 : 2000);
    };
    void tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [load, running]);

  useEffect(() => {
    const el = list.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [msgs, hidden]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(null);
    }
  };
  const patch = (label: string, body: Record<string, unknown>) => run(label, () => api(`/v1/streams/${id}/chat`, { method: 'PATCH', json: body }));
  const replace = (m: ChatOwnerMessage) => setMsgs((prev) => prev.map((x) => (x.id === m.id ? m : x)));

  const post = (e: React.FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    void run('post', async () => {
      await api(`/v1/streams/${id}/chat/messages`, { method: 'POST', json: { text: t, role } });
      setText('');
    });
  };
  const del = (m: ChatOwnerMessage) => run(`del:${m.id}`, async () => replace(await api<ChatOwnerMessage>(`/v1/streams/${id}/chat/messages/${m.id}`, { method: 'DELETE' })));
  const ban = (m: ChatOwnerMessage, deleteMessages: boolean) => {
    const scope = head?.moderation_group ? `every stream in moderation group "${head.moderation_group}"` : 'this stream';
    if (!window.confirm(`Ban ${m.nickname} on ${scope}?${deleteMessages ? ' Their messages on this stream are deleted too.' : ''} They won't be told: their messages only show to themselves.`)) return;
    void run(`ban:${m.id}`, () => api(`/v1/streams/${id}/chat/bans`, { method: 'POST', json: { message_id: m.id, delete_messages: deleteMessages } }));
  };
  const clear = () => {
    if (!window.confirm('Clear the chat? Every message so far disappears for viewers. You keep them here.')) return;
    void run('clear', () => api(`/v1/streams/${id}/chat/clear`, { method: 'POST' }));
  };

  const shown = hidden ? msgs : msgs.filter((m) => m.visible);
  const pinned = head?.pinned_message_id ? msgs.find((m) => m.id === head.pinned_message_id) : null;
  const canPost = head?.state === 'open' || head?.state === 'paused';
  const total = Object.entries(head?.reactions.total ?? {});
  const recent = Object.entries(head?.reactions.recent ?? {});

  return (
    <section className="card chat-card" aria-labelledby="h-chat">
      <div className="card-head">
        <h2 id="h-chat">
          Live chat <span className="chat-beta">beta</span>
        </h2>
        {head && (
          <span className="row" style={{ gap: 8, fontSize: 13 }}>
            <span className={`chat-state s-${head.state}`}>{STATE_TEXT[head.state] ?? head.state}</span>
            {head.busy && <span style={{ color: 'var(--warn)' }}>Very busy</span>}
          </span>
        )}
      </div>

      {head && (
        <div className="chat-bar">
          <label className="row" style={{ gap: 6 }}>
            <span className="faint">Slow mode</span>
            <select
              className="input small"
              value={head.slow_mode_seconds}
              disabled={busy !== null || !canPost}
              onChange={(e) => void patch('slow', { slow_mode_seconds: Number(e.target.value) })}
            >
              {SLOW.map((s) => (
                <option key={s} value={s}>
                  {s ? `${s} s` : 'Off'}
                </option>
              ))}
            </select>
            {head.slow_mode_auto && (
              <span className="faint" style={{ fontSize: 12 }}>
                auto {head.effective_slow_mode_seconds} s
                <Tip>Chat is very busy, so every viewer waits {head.effective_slow_mode_seconds} s between messages for now. It eases off by itself.</Tip>
              </span>
            )}
          </label>
          <button type="button" className="btn small" disabled={busy !== null || !canPost} onClick={() => void patch('pause', { paused: !head.paused })}>
            {head.paused ? 'Resume' : 'Pause'}
          </button>
          <button type="button" className="btn small" disabled={busy !== null || !canPost} onClick={clear}>
            Clear
          </button>
          <span className="grow" />
          <label className="row faint" style={{ gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
            Show hidden
          </label>
        </div>
      )}

      {pinned && (
        <div className="chat-pinned">
          <span className="faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Pinned</span>
          <span style={{ flex: 1 }}>
            <b>{pinned.nickname}</b> {pinned.text}
          </span>
          <button type="button" className="btn small" disabled={busy !== null} onClick={() => void patch('unpin', { pinned_message_id: null })}>
            Unpin
          </button>
        </div>
      )}

      <div
        className="chat-log"
        ref={list}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
      >
        {shown.length === 0 && <p className="muted" style={{ margin: 0, padding: 16 }}>{head ? 'No messages yet.' : 'Loading…'}</p>}
        {shown.map((m) => (
          <div key={m.id} className={`chat-msg${m.visible ? '' : ' is-hidden'}`}>
            <div className="chat-line">
              <span className="faint mono chat-time">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
              {m.role && <span className={`chat-role r-${m.role}`}>{m.role === 'host' ? 'Host' : 'Mod'}</span>}
              <b className="chat-nick">{m.nickname}</b>
              <span className="chat-text">{m.text}</span>
            </div>
            <div className="chat-meta">
              {m.deleted_at && (
                <span className="chat-flag">
                  deleted{m.deleted_by === 'moderator' ? ` by ${m.deleted_by_moderator?.name ?? 'a moderator'}` : ''}
                </span>
              )}
              {m.filtered && <span className="chat-flag">word filter</span>}
              {m.shadow && <span className="chat-flag">banned</span>}
              {m.cleared && <span className="chat-flag">cleared</span>}
              {m.fingerprint && (
                <span className="mono" title="The same fingerprint under another nickname is probably the same person">
                  {m.fingerprint}
                </span>
              )}
              {m.country && <span>{m.country}</span>}
              {m.network && <span className="mono">{m.network}</span>}
              {m.asn && <span className="chat-asn">{m.asn}</span>}
              {canPost && (
                <span className="chat-actions">
                  {m.visible && head?.pinned_message_id !== m.id && (
                    <button type="button" className="link" disabled={busy !== null} onClick={() => void patch(`pin:${m.id}`, { pinned_message_id: m.id })}>
                      Pin
                    </button>
                  )}
                  {!m.deleted_at && (
                    <button type="button" className="link" disabled={busy !== null} onClick={() => void del(m)}>
                      Delete
                    </button>
                  )}
                  {m.session_id && !m.shadow && (
                    <>
                      <button type="button" className="link danger" disabled={busy !== null} onClick={() => ban(m, false)}>
                        Ban
                      </button>
                      <button type="button" className="link danger" disabled={busy !== null} onClick={() => ban(m, true)}>
                        Ban + delete all
                      </button>
                    </>
                  )}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {canPost && (
        <form className="chat-post" onSubmit={post}>
          <select className="input small" value={role} onChange={(e) => setRole(e.target.value as 'host' | 'moderator')} aria-label="Post as">
            <option value="host">as Host</option>
            <option value="moderator">as Moderator</option>
          </select>
          <input
            className="input"
            value={text}
            maxLength={200}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Message viewers as ${stream.chat?.host_name || (role === 'host' ? 'Host' : 'Moderator')}…`}
          />
          <button type="submit" className="btn primary" disabled={busy !== null || !text.trim()}>
            Send
          </button>
        </form>
      )}

      {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}

      {head && (
        <div className="chat-foot">
          <span className="faint">
            {head.totals.messages_posted} messages · {head.totals.messages_shown} shown
          </span>
          {total.length > 0 && (
            <span className="faint">
              Reactions {total.map(([k, n]) => `${EMOJI[k] ?? k} ${n}`).join('  ')}
              {recent.length > 0 && <span style={{ color: 'var(--accent)' }}> · now {recent.map(([k, n]) => `${EMOJI[k] ?? k} ${n}`).join(' ')}</span>}
            </span>
          )}
          <span className="grow" />
          <button type="button" className="link" onClick={() => setPanel(panel === 'mods' ? null : 'mods')}>
            Moderators
          </button>
          <button type="button" className="link" onClick={() => setPanel(panel === 'bans' ? null : 'bans')}>
            Bans
          </button>
          <button type="button" className="link" onClick={() => setPanel(panel === 'words' ? null : 'words')}>
            Word list
          </button>
          <button type="button" className="link" disabled={exporting} onClick={() => void exportCsv(stream, setExporting, setError)}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      )}
      {panel === 'bans' && <Bans streamId={id} />}
      {panel === 'words' && <Words streamId={id} />}
      {panel === 'mods' && <Moderators streamId={id} />}
    </section>
  );
}

function Bans({ streamId }: { streamId: string }) {
  const [bans, setBans] = useState<ChatBan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setBans((await api<{ data: ChatBan[] }>(`/v1/streams/${streamId}/chat/bans`)).data);
    } catch (err) {
      setError(errText(err));
    }
  }, [streamId]);
  useEffect(() => void load(), [load]);
  const unban = async (b: ChatBan) => {
    try {
      await api(`/v1/streams/${streamId}/chat/bans/${b.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(errText(err));
    }
  };
  return (
    <div className="chat-sub">
      {error && <p className="error-text">{error}</p>}
      {!bans ? (
        <p className="muted">Loading…</p>
      ) : bans.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>Nobody is banned.</p>
      ) : (
        bans.map((b) => (
          <div key={b.id} className="row" style={{ justifyContent: 'space-between', fontSize: 13 }}>
            <span>
              <b>{b.nickname ?? b.session_id}</b> <span className="faint mono">{b.fingerprint}</span>{' '}
              <span className="faint">
                {b.scope === 'group' ? `group ${b.moderation_group}` : 'this stream'} · {new Date(b.created_at).toLocaleString()}
              </span>
            </span>
            <button type="button" className="btn small" onClick={() => void unban(b)}>
              Unban
            </button>
          </div>
        ))
      )}
    </div>
  );
}

function Words({ streamId }: { streamId: string }) {
  const [words, setWords] = useState<ChatWords | null>(null);
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<ChatWords>(`/v1/streams/${streamId}/chat/words`)
      .then((w) => {
        setWords(w);
        setDraft(w.words.join('\n'));
      })
      .catch((err) => setError(errText(err)));
  }, [streamId]);
  const save = async () => {
    try {
      const w = await api<ChatWords>(`/v1/streams/${streamId}/chat/words`, { method: 'PUT', json: { words: draft.split('\n').map((x) => x.trim()).filter(Boolean) } });
      setWords(w);
      setDraft(w.words.join('\n'));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(errText(err));
    }
  };
  return (
    <div className="chat-sub">
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        One word or phrase per line. Messages that contain one are kept for you and never shown to viewers.
        {words?.scope === 'group' && ` Shared by every stream in moderation group "${words.moderation_group}".`}
        {words && (words.builtin_word_filter ? ' The built-in Turkish and English list applies too.' : ' The built-in list is off for this stream.')}
      </p>
      <textarea className="input" rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={'spoiler\nbad phrase'} />
      {error && <p className="error-text">{error}</p>}
      <div className="row">
        <button type="button" className="btn small" onClick={() => void save()} disabled={!words}>
          Save word list
        </button>
        {saved && <span className="faint" style={{ fontSize: 12 }}>Saved</span>}
      </div>
    </div>
  );
}

/** The whole chat log as CSV (every message, hidden ones too), built here from the feed. */
async function exportCsv(stream: Stream, setBusy: (b: boolean) => void, setError: (e: string | null) => void) {
  setBusy(true);
  try {
    const all: ChatOwnerMessage[] = [];
    let after = 0;
    for (let page = 0; page < 200; page++) {
      const f = await api<ChatFeed>(`/v1/streams/${encodeURIComponent(stream.id)}/chat?after=${after}&limit=500`);
      all.push(...f.messages);
      after = f.next_after;
      if (!f.has_more) break;
    }
    const cols = ['seq', 'id', 'created_at', 'nickname', 'role', 'text', 'visible', 'deleted_at', 'deleted_by', 'filtered', 'shadow', 'cleared', 'session_id', 'fingerprint', 'network', 'country', 'asn'] as const;
    const cell = (v: unknown) => {
      const t = v === null || v === undefined ? '' : String(v);
      // Quoted always; a leading = + - @ is defused so a spreadsheet never runs it as a formula.
      return `"${(/^[=+\-@\t\r]/.test(t) ? `'${t}` : t).replace(/"/g, '""')}"`;
    };
    const rows = all.map((m) =>
      cols.map((c) => cell(c === 'deleted_by' ? (m.deleted_by === 'moderator' ? `moderator: ${m.deleted_by_moderator?.name ?? ''}` : m.deleted_by) : m[c])).join(','),
    );
    const csv = '\ufeff' + [cols.join(','), ...rows].join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `chat-${stream.id}.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  } catch (err) {
    setError(errText(err));
  } finally {
    setBusy(false);
  }
}

const MOD_STATUS: Record<string, string> = { invited: 'Link not opened yet', active: 'Active', expired: 'Expired', revoked: 'Removed' };
/** How long a moderator stays one after opening their link. */
const MOD_DAYS = [
  { days: 1, label: '1 day (one show)' },
  { days: 7, label: '7 days' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
];

/** Moderator links: one per person, single-use, revocable. */
function Moderators({ streamId }: { streamId: string }) {
  const [mods, setMods] = useState<ChatModerator[] | null>(null);
  const [name, setName] = useState('');
  const [days, setDays] = useState(30);
  const [created, setCreated] = useState<ChatModerator | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setMods((await api<{ data: ChatModerator[] }>(`/v1/streams/${streamId}/chat/moderators`)).data);
    } catch (err) {
      setError(errText(err));
    }
  }, [streamId]);
  useEffect(() => void load(), [load]);
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      setCreated(await api<ChatModerator>(`/v1/streams/${streamId}/chat/moderators`, { method: 'POST', json: { name: name.trim(), active_days: days } }));
      setName('');
      await load();
    } catch (err) {
      setError(errText(err));
    }
  };
  const revoke = async (m: ChatModerator) => {
    if (!window.confirm(`Remove ${m.name} as a moderator? Their link and session stop working at once.`)) return;
    try {
      await api(`/v1/streams/${streamId}/chat/moderators/${m.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(errText(err));
    }
  };
  // Removed and expired ones are history: invite them again to bring them back.
  const live = mods?.filter((m) => m.status === 'invited' || m.status === 'active') ?? [];
  return (
    <div className="chat-sub">
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Send someone a link: they moderate from the chat page (delete, ban, pin, slow mode, pause) with a Moderator badge, without a dashboard account. Each link
        works once, in one browser, and expires after 7 days unused. Once opened, they moderate for the days you choose; then invite them again if you want them back.
        {live[0]?.scope === 'group' && ` Moderators here cover every stream in moderation group "${live[0].moderation_group}".`}
      </p>
      <form className="chat-post" onSubmit={create}>
        <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="Moderator's name, e.g. Mert" />
        <select className="input" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="How long they moderate" style={{ width: 'auto' }}>
          {MOD_DAYS.map((d) => (
            <option key={d.days} value={d.days}>
              {d.label}
            </option>
          ))}
        </select>
        <button type="submit" className="btn small" disabled={!name.trim()}>
          Create link
        </button>
      </form>
      {created?.invite_url && (
        <CopyField
          label={`Link for ${created.name}`}
          value={created.invite_url}
          oneLine
          secret
          tip="Shown only now. Send it to them privately: whoever opens it first becomes this moderator."
        />
      )}
      {error && <p className="error-text">{error}</p>}
      {live.map((m) => (
        <div key={m.id} className="row" style={{ justifyContent: 'space-between', fontSize: 13 }}>
          <span>
            <b>{m.name}</b>{' '}
            <span className="faint">
              {MOD_STATUS[m.status] ?? m.status}
              {m.status === 'active' && m.active_until ? ` until ${new Date(m.active_until).toLocaleDateString()}` : ''}
              {m.status === 'invited' ? ` · ${m.active_days} ${m.active_days === 1 ? 'day' : 'days'} once opened` : ''}
              {m.last_seen_at ? ` · last seen ${new Date(m.last_seen_at).toLocaleString()}` : ''}
            </span>
          </span>
          <button type="button" className="btn small" onClick={() => void revoke(m)}>
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}
